/**
 * Practice by a share link (ADR-115 Decision 10, Implementation note 4; handoff §17.2):
 * the player's actions for a reader who has not saved the text.
 *
 * The server keeps nothing for this run. Every verdict is still the server's (the
 * stateless check route, ADR-116 Decision 3); what the owner's attempt would store, the
 * gap states, the wrong answers, the run's counts, lives here in memory and is gone when
 * the page is left. The run is always under level-1 rules: the bank is open from the
 * start, there is no level to change, no «Мой ответ подходит», and nothing to resume.
 *
 * It answers with the owner's shapes (`PracticeView`, `PracticeVerdictResponse`,
 * `PracticeCompletion`), so the player runs it unchanged.
 */
import { useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { checkSharedAnswer, fetchSharedBank } from './useTextShare'
import type { PracticeActions } from './useTextPractice'
import type {
  PracticeAttemptState, PracticeBankChip, PracticeCompletion, PracticeGap, PracticeVerdictResponse, PracticeView, SharedText,
} from '../types'

/** The unsaved run's level: shown nowhere, and fixed (handoff §17.2). */
const SHARED_LEVEL = 1
const MAX_LEVEL = 3

const done = (g: PracticeGap) => g.state !== 'empty'

function freshGaps(shared: SharedText): PracticeGap[] {
  return shared.gaps.map((g) => ({
    id: g.id, places: g.places, label: g.label, state: 'empty', answer: null, written: [],
    first_letter: null, wrong_count: 0, outcome: null,
  }))
}

interface Run {
  gaps: PracticeGap[]
  attempt: PracticeAttemptState | null
  bank: PracticeBankChip[] | null
}

/** A chip is struck once a done gap holds its word: a display aid, never a verdict. */
function markUsed(bank: PracticeBankChip[] | null, gaps: PracticeGap[]): PracticeBankChip[] | null {
  if (!bank) return bank
  const filled = new Set(gaps.filter(done).map((g) => (g.answer ?? '').trim().toLowerCase()))
  return bank.map((c) => ({ ...c, used: filled.has(c.form.trim().toLowerCase()) }))
}

function counted(attempt: PracticeAttemptState, gaps: PracticeGap[], activeId?: string | null): PracticeAttemptState {
  const answered = gaps.filter(done).length
  return { ...attempt, answered, remaining: gaps.length - answered, active_gap_id: activeId ?? attempt.active_gap_id }
}

export function useSharedPractice(token: string, shared: SharedText): { view: PracticeView; actions: PracticeActions } {
  const [run, setRun] = useState<Run>(() => ({ gaps: freshGaps(shared), attempt: null, bank: null }))
  // The latest run, for answers judged after a re-render the closure has not seen.
  const runRef = useRef(run)
  const commit = (next: Run) => { runRef.current = next; setRun(next) }

  const view = useMemo<PracticeView>(() => ({
    text_id: '',
    title: shared.title,
    level: SHARED_LEVEL,
    max_level: MAX_LEVEL,
    segments: shared.segments,
    gaps: run.gaps,
    bank_state: 'open',
    bank: run.bank,
    attempt: run.attempt,
    can_accept: false,
  }), [shared.title, shared.segments, run])

  const viewOf = (r: Run): PracticeView => ({ ...view, gaps: r.gaps, bank: r.bank, attempt: r.attempt })

  const start = useMutation<PracticeView, Error, void>({
    mutationFn: async () => {
      // A bank that fails to load leaves the row empty; the run itself still works.
      const forms = await fetchSharedBank(token).catch(() => [] as string[])
      const gaps = freshGaps(shared)
      const attempt: PracticeAttemptState = {
        id: 'shared', started_at: new Date().toISOString(), active_gap_id: gaps[0]?.id ?? null,
        bank_opened: false, answered: 0, remaining: gaps.length,
      }
      const next = { gaps, attempt, bank: forms.map((form) => ({ form, used: false })) }
      commit(next)
      return viewOf(next)
    },
  })

  const answer = useMutation<PracticeVerdictResponse, Error, { gapId: string; answer: string; expectedWrongCount: number }>({
    mutationFn: async ({ gapId, answer: typed }) => {
      const before = runRef.current.gaps.find((g) => g.id === gapId)
      if (!before || !runRef.current.attempt) throw new Error('No such gap')
      const result = await checkSharedAnswer(token, gapId, typed, before.wrong_count)
      const cur = runRef.current
      // The run was left while the answer was out: it has nothing to write into.
      if (!cur.attempt) throw new Error('Run ended')
      const gap = cur.gaps.find((g) => g.id === gapId) ?? before
      let next: PracticeGap = gap
      if (result.verdict === 'correct') {
        next = {
          ...gap, state: 'answered', answer: typed.trim(), written: result.written, first_letter: null,
          outcome: gap.wrong_count === 0 && gap.first_letter === null ? 'first' : 'hinted',
        }
      } else if (result.verdict === 'fits') {
        next = { ...gap, first_letter: result.first_letter }
      } else if (result.verdict === 'wrong') {
        next = { ...gap, wrong_count: gap.wrong_count + 1, first_letter: result.first_letter }
      } else if (result.verdict === 'revealed') {
        next = {
          ...gap, state: 'revealed', answer: result.answer, written: result.written, first_letter: null,
          wrong_count: gap.wrong_count + 1, outcome: 'revealed',
        }
      }
      const gaps = cur.gaps.map((g) => (g.id === gapId ? next : g))
      const attempt = counted(cur.attempt, gaps)
      const bank = markUsed(cur.bank, gaps)
      commit({ gaps, attempt, bank })
      return { verdict: result.verdict, gap: next, attempt, bank }
    },
  })

  // Not offered by link (`can_accept` is false); present only so the player's shape holds.
  const accept = useMutation<PracticeVerdictResponse, Error, { gapId: string; answer: string }>({
    mutationFn: () => Promise.reject(new Error('Not offered by link')),
  })
  // The bank is open from the start.
  const openBank = useMutation<PracticeView, Error, void>({
    mutationFn: async () => viewOf(runRef.current),
  })
  // Nothing to resume, so the active gap is not kept anywhere.
  const setActive = useMutation<void, Error, string>({
    mutationFn: async (gapId) => {
      const cur = runRef.current
      if (cur.attempt) commit({ ...cur, attempt: counted(cur.attempt, cur.gaps, gapId) })
    },
  })

  const complete = useMutation<PracticeCompletion, Error, void>({
    mutationFn: async () => {
      const gaps = runRef.current.gaps
      return {
        attempt_id: 'shared',
        total: gaps.length,
        first_time_count: gaps.filter((g) => g.outcome === 'first').length,
        hinted_count: gaps.filter((g) => g.outcome === 'hinted').length,
        revealed_count: gaps.filter((g) => g.outcome === 'revealed').length,
        bank_opened: true,
        level_before: SHARED_LEVEL,
        level_after: SHARED_LEVEL,
        max_level: MAX_LEVEL,
      }
    },
  })

  const restart = useMutation<void, Error, void>({
    mutationFn: async () => { commit({ gaps: freshGaps(shared), attempt: null, bank: null }) },
  })

  return { view, actions: { start, answer, accept, openBank, setActive, complete, restart } }
}
